/**
 * Checkout-adjacent screens (address/COD/pincode/order summary), order
 * success + tracking, sign-in / OTP, and customer account routes.
 *
 * Prefix: "ca."
 */

export const checkoutAccount = {
  en: {
    // ---------- Pincode / delivery bar (PDP) ----------
    "ca.pincode.title": "Delivery & payment",
    "ca.pincode.placeholder": "Enter pincode",
    "ca.pincode.check": "Check",
    "ca.pincode.change": "Change",
    "ca.pincode.eta": "Delivered in 2-4 days",
    "ca.pincode.toCity": "to {city}",
    "ca.pincode.pinLabel": "PIN {pincode}",
    "ca.pincode.codAvailable": "COD available",
    "ca.pincode.emiPayIn3": "EMI & Pay-in-3",
    "ca.pincode.freeShipping": "Free shipping",

    // ---------- Payment method toggle ----------
    "ca.payment.prepay": "Prepay (UPI/Card)",
    "ca.payment.prepayHint": "Instant + 5% benefit",
    "ca.payment.cod": "Cash on Delivery",
    "ca.payment.codHint": "Pay upon delivery",

    // ---------- Order success ----------
    "ca.success.thankYou": "Thank you{name}",
    "ca.success.confirmed": "Your order is confirmed. Your wellness journey begins today.",
    "ca.success.order": "Order",
    "ca.success.estimatedDelivery": "Estimated delivery",
    "ca.success.shippingTo": "Shipping to",
    "ca.success.loading": "Loading…",
    "ca.success.loadingOrder": "Loading your order…",
    "ca.success.loadError": "We couldn't load your order details.",
    "ca.success.progressEyebrow": "Progress",
    "ca.success.progressTitle": "Where your order is",
    "ca.success.stage.confirmed": "Confirmed",
    "ca.success.stage.packing": "Packing",
    "ca.success.stage.shipped": "Shipped",
    "ca.success.stage.out": "Out for delivery",
    "ca.success.stage.delivered": "Delivered",
    "ca.success.boxEyebrow": "In your box",
    "ca.success.boxTitle": "Your protocol",
    "ca.success.qty": "Qty {count}",
    "ca.success.pillProtocol": "Protocol",
    "ca.success.pillArrives": "Arrives",
    "ca.success.vitaHello": "Hi, I'm Vita 👋",
    "ca.success.vitaIntro": "I'll guide your wellness journey, one calm day at a time.",
    "ca.success.vitaRemind": "Remind me daily",
    "ca.success.vitaTrack": "Track my progress",
    "ca.success.vitaExplain": "Explain my supplements",
    "ca.success.vitaAsk": "Ask a question",
    "ca.success.meetVita": "Meet Vita",
    "ca.success.routineEyebrow": "Daily ritual",
    "ca.success.routineTitle": "Your routine, planned",
    "ca.success.morning": "Morning",
    "ca.success.afternoon": "Afternoon",
    "ca.success.night": "Night",
    "ca.success.restDay": "rest day",
    "ca.success.didYouKnow": "Did you know?",
    "ca.success.supportEyebrow": "Support",
    "ca.success.needHelp": "Need a hand?",
    "ca.success.chatVita": "Chat with Vita",
    "ca.success.chatVitaSub": "Quick answers, anytime",
    "ca.success.trackOrder": "Track order",
    "ca.success.trackOrderSub": "Live shipment updates",
    "ca.success.contactSupport": "Contact support",
    "ca.success.contactSupportSub": "We reply within 4 hours",
    "ca.success.trackMyOrder": "Track my order",
    "ca.success.exploreScience": "Explore science",

    // ---------- Track order ----------
    "ca.track.title": "Track your order{name}",
    "ca.track.backToOrder": "Back to order",
    "ca.track.backToHome": "Back to home",
    "ca.track.orderLabel": "Order",
    "ca.track.shippingTo": "Shipping to {city}",
    "ca.track.enterPrompt": "Enter your order ID with the email or phone used at checkout.",
    "ca.track.orderIdLabel": "Order ID",
    "ca.track.orderIdPlaceholder": "TW-1042 or #1042",
    "ca.track.email": "Email",
    "ca.track.phone": "Phone",
    "ca.track.emailUsed": "Email used at checkout",
    "ca.track.phoneUsed": "Phone used at checkout",
    "ca.track.emailPlaceholder": "you@email.com",
    "ca.track.phonePlaceholder": "+91 98765 43210",
    "ca.track.submit": "Track order",
    "ca.track.lookingUp": "Looking up…",
    "ca.track.orderIdHint": "Your order ID is in your confirmation email and on the order success page.",
    "ca.track.loading": "Loading tracking…",
    "ca.track.errorNotFound": "We couldn't find that order. Double-check the order ID and the email or phone used at checkout.",
    "ca.track.errorGeneric": "Tracking unavailable right now.",
    "ca.track.errorEnterOrderId": "Enter your order ID.",
    "ca.track.errorEnterEmail": "Enter the email used at checkout.",
    "ca.track.errorEnterPhone": "Enter the phone number used at checkout.",
    "ca.track.contactSupport": "Contact support",
    "ca.track.whatsappUs": "WhatsApp us",
    "ca.track.carrierTracking": "Carrier tracking",
    "ca.track.openCarrierTracking": "Open carrier tracking",
    "ca.track.lastUpdated": "Last updated {rel} · {time}",
    "ca.track.mayBeDelayed": " · Tracking may be delayed",
    "ca.track.courier": "Courier",
    "ca.track.awb": "AWB",
    "ca.track.shipped": "Shipped",
    "ca.track.delivered": "Delivered",
    "ca.track.assignedSoon": "Assigned soon",
    "ca.track.historyEyebrow": "History",
    "ca.track.historyTitle": "Latest shipment updates",
    "ca.track.noUpdatesYet": "No carrier updates yet. We'll post new events here as soon as your shipment moves.",
    "ca.track.update": "Update",

    // ---------- Auth / sign-in ----------
    "ca.auth.signInWhatsapp": "Sign in with WhatsApp",
    "ca.auth.signIn": "Sign in",
    "ca.auth.createAccount": "Create account",
    "ca.auth.subWhatsapp": "Get a one-time code on WhatsApp, no password to remember.",
    "ca.auth.subSignin": "Access your order history and reorder in one tap.",
    "ca.auth.subSignup": "Save your details and track every order in one place.",
    "ca.auth.welcomeBonus": "🎁 ₹100 welcome bonus in TruCoins",
    "ca.auth.referralNote": "A friend invited you with code {code}. Create your account and ₹100 in TruCoins lands in your wallet instantly, ready to spend on your first order. One bonus per phone number.",
    "ca.auth.whatsappOtp": "WhatsApp OTP",
    "ca.auth.emailPassword": "Email & password",
    "ca.auth.email": "Email",
    "ca.auth.mobileNumber": "Mobile number",
    "ca.auth.mobilePlaceholder": "10-digit Indian mobile",
    "ca.auth.mobileHint": "We use this to link guest orders and send delivery updates on WhatsApp.",
    "ca.auth.password": "Password",
    "ca.auth.newToTruwe": "New to TruWe? Create an account",
    "ca.auth.alreadyHaveAccount": "Already have an account? Sign in",
    "ca.auth.backToStore": "← Back to store",
    "ca.auth.invalidPhone": "Please enter a valid 10-digit Indian mobile number",
    "ca.auth.accountCreated": "Account created. Check your email if confirmation is required.",
    "ca.auth.somethingWrong": "Something went wrong",
    "ca.auth.walletBonusCredited": "🎁 ₹{amount} in TruCoins added to your wallet",
    "ca.auth.welcomeBonusClaimed": "Welcome bonus already claimed on this phone number.",
    // WhatsApp OTP form
    "ca.auth.mobileNumberField": "Mobile number",
    "ca.auth.whatsappHint": "We'll message a one-time code on WhatsApp. Standard messaging rates may apply.",
    "ca.auth.codeSent": "Code sent on WhatsApp",
    "ca.auth.newCodeSent": "New code sent on WhatsApp",
    "ca.auth.sixDigitCode": "6-digit code",
    "ca.auth.notReceived": "Didn't get it? Check WhatsApp on this number.",
    "ca.auth.resendIn": "Resend in {seconds}s",
    "ca.auth.resendCode": "Resend code",
    "ca.auth.sendOtp": "Send OTP on WhatsApp",
    "ca.auth.sending": "Sending…",
    "ca.auth.verifying": "Verifying…",
    "ca.auth.verifyContinue": "Verify & continue",
    "ca.auth.changeNumber": "Change number",
    "ca.auth.otpDisabled": "WhatsApp OTP is not configured for this environment. Please use email & password to sign in, or try again later.",
    "ca.auth.signedIn": "Signed in",
    "ca.auth.oneLastStep": "One last step, tell us where to email your receipts.",
    "ca.auth.numberVerified": "✓ WhatsApp number verified. Add your name to finish, email is optional.",
    "ca.auth.yourName": "Your name",
    "ca.auth.namePlaceholder": "e.g. Priya Sharma",
    "ca.auth.emailOptional": "Email (optional)",
    "ca.auth.emailOptionalHint": "Add it to receive order receipts and delivery updates by email, we'll still message you on WhatsApp.",
    "ca.auth.creatingAccount": "Creating account…",
    "ca.auth.createContinue": "Create account & continue",

    // ---------- Account: overview ----------
    "ca.account.couldntLoad": "Couldn't load your account",
    "ca.account.retry": "Retry",
    "ca.account.notFound": "Not found",
    "ca.account.signedOut": "Signed out",
    "ca.account.welcomeBack": "Welcome back",
    "ca.account.hi": "Hi, {name}",
    "ca.account.admin": "Admin",
    "ca.account.signOut": "Sign out",
    "ca.account.out": "Out",
    "ca.account.orderHistory": "Order history",
    "ca.account.orderHistorySub": "Every TruWe order, tracked in one place.",
    "ca.account.notificationPrefs": "Notification preferences",
    "ca.account.yourDietPlans": "Your diet plans",
    "ca.account.yourRequests": "Your requests",
    "ca.account.shareExperience": "Share your experience",
    "ca.account.shareExperienceSub": "Delivered products you can review — every review earns {coins} TruCoins, whatever the rating. Honest feedback helps more than a high score.",
    "ca.account.submitted": "Submitted",
    "ca.account.write": "Write",
    "ca.account.orderServiceDown": "We couldn't reach the order service right now. Please try again in a moment.",
    "ca.account.noOrdersYet": "No orders yet under {email}.",
    "ca.account.ordersAppearHint": "Orders placed with this email at checkout will appear here automatically.",
    "ca.account.exploreProducts": "Explore products",
    "ca.account.placed": "Placed {date}",
    "ca.account.productsUnavailable": "These products are no longer available.",
    "ca.account.couldntAddCart": "Couldn't add to cart. Please try again.",
    "ca.account.addedToCart": "Added to cart",
    "ca.account.trackOrderLink": "Track order",
    "ca.account.returnRefund": "Return / refund",
    "ca.account.returnPeriodOver": "Return period over",
    "ca.account.buyAgain": "Buy again",
    "ca.account.daysLeft": "{count} day left",
    "ca.account.daysLeft_plural": "{count} days left",
    // Shipment strip
    "ca.account.shipment": "Shipment",
    "ca.account.courierLabel": "Courier:",
    "ca.account.awbLabel": "AWB:",
    "ca.account.carrierPage": "Carrier page",
    // TruCoins
    "ca.account.trucoins": "TruCoins",
    "ca.account.available": "Available",
    "ca.account.pending": "Pending",
    "ca.account.reserved": "Reserved",
    "ca.account.reservedHint": "Held for a pending checkout.",
    "ca.account.lifetimeEarned": "Lifetime earned",
    "ca.account.lifetimeRedeemed": "Lifetime redeemed",
    "ca.account.activity": "Activity",
    "ca.account.noActivityYet": "No TruCoins activity yet.",
    "ca.account.showMore": "Show more ({count} remaining)",
    "ca.account.loadingMore": "Loading…",
    "ca.account.couldntLoadTrucoins": "Couldn't load your TruCoins right now.",
    "ca.account.noTrucoinsAccount": "No TruCoins account yet — place a prepaid order to start earning.",
    // Refer & earn
    "ca.account.referEarn": "Refer & Earn",
    "ca.account.referEarnSub": "Give ₹100 off · Get TruCoins",
    "ca.account.open": "Open →",
    // Referral timeline
    "ca.account.yourReferrals": "Your referrals",
    "ca.account.total": "{count} total",
    "ca.account.couldntLoadReferrals": "Couldn't load your referrals.",
    "ca.account.noReferralsYet": "No one has signed up with your code yet. Share your link to start earning.",
    "ca.account.via": "via {code}",
    "ca.account.status.invited": "Invited",
    "ca.account.status.signedUp": "Signed up",
    "ca.account.status.orderPlaced": "Order placed",
    "ca.account.status.credited": "Credited",
    "ca.account.status.creditedAmount": "Credited {amount}",
    "ca.account.status.reversed": "Reversed",
    "ca.account.status.notEligible": "Not eligible",
    "ca.account.status.underReview": "Under review",
    "ca.account.rewardReversed": "Reward reversed{reason}{date}.",
    "ca.account.underReviewByTeam": "Under review by our team{reason}.",
    "ca.account.waitingFirstOrder": "Waiting for their first paid order",
    "ca.account.creditInProgress": "TruCoins credit in progress",

    // ---------- Notifications preferences ----------
    "ca.notif.couldntLoad": "Couldn't load your preferences",
    "ca.notif.tryAgain": "Try again",
    "ca.notif.backToAccount": "Back to your account",
    "ca.notif.heading": "How we keep in touch",
    "ca.notif.body": "Pick the channels that suit you. Changes save straight away, and order-critical messages (payment, delivery, refunds) are always sent no matter what you choose here.",
    "ca.notif.footer": "Every change is recorded with a timestamp so we can honour it across WhatsApp, email and browser notifications. Read more in our {link}.",
    "ca.notif.privacyPolicy": "privacy policy",

    // ---------- Diet plans ----------
    "ca.plans.backToAccount": "Back to account",
    "ca.plans.allPlans": "All plans",
    "ca.plans.heading": "Your diet plans",
    "ca.plans.loading": "Loading…",
    "ca.plans.noPlansYet": "You haven't generated a plan yet.",
    "ca.plans.buildFreePlan": "Build a free plan",
    "ca.plans.defaultHeadline": "7-day plan",

    // ---------- Refunds ----------
    "ca.refunds.backToAccount": "Back to account",
    "ca.refunds.heading": "Refund requests",
    "ca.refunds.windowNote": "Raise a request within {days} days of delivery. Every request is reviewed by a human — submitting one does not automatically refund your payment.",
    "ca.refunds.startNew": "Start a new request",
    "ca.refunds.checkingOrders": "Checking your recent orders…",
    "ca.refunds.noneEligible": "No orders are currently eligible. Requests can be raised for paid orders delivered in the last {days} days.",
    "ca.refunds.order": "Order",
    "ca.refunds.chooseOrder": "Choose an eligible order",
    "ca.refunds.daysLeftOption": "{days} day left",
    "ca.refunds.daysLeftOption_plural": "{days} days left",
    "ca.refunds.itemsToRefund": "Items to refund",
    "ca.refunds.ordered": "Ordered {qty} · {price} each",
    "ca.refunds.qty": "Qty {n}",
    "ca.refunds.reason": "Reason",
    "ca.refunds.whatWentWrong": "What went wrong?",
    "ca.refunds.describeIssue": "Describe the issue",
    "ca.refunds.describePlaceholder": "Tell us what happened so our team can resolve it quickly.",
    "ca.refunds.photos": "Photos ({count}/{max})",
    "ca.refunds.photosHint": "1–4 clear photos are required. Include the product and the outer pack.",
    "ca.refunds.add": "Add",
    "ca.refunds.removePhoto": "Remove photo",
    "ca.refunds.reviewNote": "Submitting records your request only. Our team reviews it manually and no money moves until it is approved.",
    "ca.refunds.uploadingPhotos": "Uploading photos…",
    "ca.refunds.submitRequest": "Submit refund request",
    "ca.refunds.trackYourRequests": "Track your requests",
    "ca.refunds.historySummary": "You have {count} refund request. See status and full history in one place.",
    "ca.refunds.historySummary_plural": "You have {count} refund requests. See status and full history in one place.",
    "ca.refunds.historyEmpty": "All your return, refund and cancellation requests — with status history — live here.",
    "ca.refunds.viewRequests": "View your requests",
    "ca.refunds.imagesOnly": "Only image files can be attached.",
    "ca.refunds.tooLarge": "{name} is larger than 8 MB.",
    "ca.refunds.selectOrder": "Select an order.",
    "ca.refunds.selectItem": "Select at least one item.",
    "ca.refunds.pickReason": "Pick a reason.",
    "ca.refunds.moreDetail": "Tell us a bit more (10+ characters).",
    "ca.refunds.addPhoto": "Add at least one photo.",
    "ca.refunds.uploadFailed": "Photo upload failed. Please try again.",
    "ca.refunds.signInAgain": "Please sign in again.",
    "ca.refunds.requestSubmitted": "Request {ticket} submitted. We'll review it manually.",

    // ---------- Requests ----------
    "ca.requests.backToAccount": "Back to account",
    "ca.requests.heading": "Your requests",
    "ca.requests.body": "Track every return, refund and cancellation request in one place.",
    "ca.requests.openCount": "{count} open request.",
    "ca.requests.openCount_plural": "{count} open requests.",
    "ca.requests.raiseReturn": "Raise a return / refund",
    "ca.requests.loading": "Loading your requests…",
    "ca.requests.noneYet": "You haven't raised any requests yet.",
    "ca.requests.hideHistory": "Hide status history",
    "ca.requests.viewHistory": "View status history",
    "ca.requests.loadingHistory": "Loading history…",
    "ca.requests.noUpdatesYet": "No updates yet.",
    "ca.requests.kindRefund": "Return / refund",
    "ca.requests.kindCancellation": "Cancellation",
    "ca.requests.raised": "Raised {date}",
    "ca.requests.photoCount": "{count} photo",
    "ca.requests.photoCount_plural": "{count} photos",
    "ca.requests.reasonLabel": "Reason: {reason}",
    "ca.requests.updateFromTruwe": "Update from TruWe: ",
    "ca.requests.defaultHelp": "We'll keep you posted on WhatsApp.",

    // ---------- Referral page ----------
    "ca.referral.couldntLoad": "Couldn't load Refer & Earn",
    "ca.referral.retry": "Retry",
    "ca.referral.backToAccount": "Back to account",
    "ca.referral.heading": "Refer & Earn",
    "ca.referral.sub": "Give {amount} off · Get TruCoins for every friend who buys",
    "ca.referral.loadingStatus": "Loading your referral status…",
    "ca.referral.couldntLoadStatus": "Couldn't load your referral status.",
    "ca.referral.earnedFromReferrals": "Earned from referrals",
    "ca.referral.pendingRewards": "Pending rewards",
    "ca.referral.noneYet": "None yet",
    "ca.referral.friendInProgress": "{count} friend in progress",
    "ca.referral.friendInProgress_plural": "{count} friends in progress",
    "ca.referral.successfulReferrals": "Successful referrals",
    "ca.referral.shareToStart": "Share your link to start",
    "ca.referral.nextReward": "Next reward: {amount}",
    "ca.referral.yourLink": "Your referral link",
    "ca.referral.linkExplainer": "Friends get {amount} off their first eligible prepaid order of ₹1,000+ ex-GST. You earn TruCoins 7 days after their delivery.",
    "ca.referral.status": "Referral status",
    "ca.referral.trucoinsBalance": "TruCoins balance:",
    "ca.referral.available": "available.",
    "ca.referral.viewWallet": "View wallet",
    "ca.referral.linkCopied": "Link copied",
    "ca.referral.couldntCopy": "Couldn't copy link",
    "ca.referral.settingUp": "Your referral code is being set up",
    "ca.referral.copied": "Copied",
    "ca.referral.copyLink": "Copy link",
    "ca.referral.share": "Share",
    "ca.referral.copyReferralLink": "Copy referral link",
    "ca.referral.linkCopiedAria": "Link copied",
    "ca.referral.noReferralsYet": "No referrals yet.",
    "ca.referral.shareWatchGrow": "Share your link with friends and watch your TruCoins grow.",
    "ca.referral.via": "via {code}",
    "ca.referral.referral": "referral",

    // ---------- Review ----------
    "ca.review.heading": "Your reviews",
    "ca.review.sub": "Share your experience with the protocols you've ordered.",
    "ca.review.backToAccount": "← Account",
    "ca.review.earnBanner": "Every review earns {coins} TruCoins — including 1-star ones",
    "ca.review.earnBannerSub": "We reward you for taking the time to write, not for a good rating. {coins} TruCoins ({amount}) are credited whatever you score us. We only remove spam or abuse — never honest criticism.",
    "ca.review.loading": "Loading…",
    "ca.review.noneYet": "No reviewable products yet.",
    "ca.review.unlockHint": "Reviews unlock once your order is paid.",
    "ca.review.order": "Order {name}",
    "ca.review.submitted": "Submitted {date}",
    "ca.review.thankYouCredit": "Thank you for taking the time to write this. Your {coins} TruCoins will be credited shortly — they are the same for every rating.",
    "ca.review.viewProduct": "View product",
    "ca.review.tapToRate": "Tap to rate",
    "ca.review.starRating": "{n} / 5",
    "ca.review.headlinePlaceholder": "Headline (optional)",
    "ca.review.commentPlaceholder": "What did you notice? Sleep, energy, digestion, skin, cycles…",
    "ca.review.uploading": "Uploading…",
    "ca.review.addPhoto": "+ Add photo",
    "ca.review.remove": "Remove",
    "ca.review.submitting": "Submitting…",
    "ca.review.submit": "Submit review",
    "ca.review.pickRating": "Please pick a star rating",
    "ca.review.shortComment": "Please share a short comment",
    "ca.review.couldNotSubmit": "Could not submit",
    "ca.review.thankYouToast": "Thank you for taking the time to write this. Your {coins} TruCoins will be credited shortly.",
    "ca.review.maxPhotos": "Max 2 photos",
    "ca.review.maxSize": "Max 3 MB per photo",
    "ca.review.uploadFailed": "Upload failed",
  },
} as const;
